import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { AccountSlugRefusal } from 'fym-shared/src/music24/accountSlug';
import { TypedMemo } from '../../common/types';
import { AddressState } from '../../smartLinks/profile/useAccountSlugCheck';
import { accountSlugRefusalText } from '../../smartLinks/profile/accountSlugRefusalText';
import { welcomeMessages as messages } from './welcomeMessages';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The live check states plus `reserved`, which only the save answers today (design: onboarding a6). */
export type AddressStatusKind = AddressState | 'reserved';

interface AddressStatusProps {
  kind: AddressStatusKind;
  /** Why the address cannot be used: the `illegal` line names the reason (QA brk-r2-4). */
  refusal?: AccountSlugRefusal | null;
  suggestion?: string;
  onUseSuggestion: (slug: string) => void;
  /** Only the chip, for the one-line address of a1. */
  chipOnly?: boolean;
}

/** The status chip and the line under a bad address (design: onboarding a6, `.ob-st`). */
const AddressStatusComponent: React.FC<AddressStatusProps> = ({
  kind,
  refusal = null,
  suggestion,
  onUseSuggestion,
  chipOnly = false,
}) => {
  const intl = useIntl();

  const renderChip = (): React.ReactNode => {
    switch (kind) {
      case AddressState.free:
        return (
          <span className={clsx(styles.chip, styles.chipFree)}>{intl.formatMessage(messages.addressAvailable)}</span>
        );
      case AddressState.checking:
        return (
          <span className={styles.chip}>
            <i className={styles.spinner} aria-hidden="true" />
            {intl.formatMessage(messages.addressChecking)}
          </span>
        );
      case AddressState.taken:
        return <span className={clsx(styles.chip, styles.chipBad)}>{intl.formatMessage(messages.addressTaken)}</span>;
      case AddressState.illegal:
        return <span className={clsx(styles.chip, styles.chipBad)}>{intl.formatMessage(messages.addressIllegal)}</span>;
      case 'reserved':
        return (
          <span className={clsx(styles.chip, styles.chipBad)}>{intl.formatMessage(messages.addressReserved)}</span>
        );
      case AddressState.idle:
        return null;
    }
  };

  const renderLine = (): React.ReactNode => {
    switch (kind) {
      case AddressState.taken:
        return (
          <>
            {intl.formatMessage(messages.addressTakenLine)}{' '}
            {suggestion !== undefined ? (
              <button type="button" className={styles.link} onClick={() => onUseSuggestion(suggestion)}>
                {intl.formatMessage(messages.addressUseSuggestion, { slug: suggestion })}
              </button>
            ) : null}
          </>
        );
      case AddressState.illegal:
        return refusal === null || refusal === 'reserved'
          ? intl.formatMessage(messages.addressIllegalLine)
          : accountSlugRefusalText(intl, refusal);
      case 'reserved':
        return intl.formatMessage(messages.addressReservedLine);
      case AddressState.idle:
      case AddressState.checking:
      case AddressState.free:
        return null;
    }
  };

  const chip = renderChip();
  if (chipOnly) {
    return <>{chip}</>;
  }
  const line = renderLine();
  if (chip === null && line === null) {
    return null;
  }
  return (
    <div className={styles.statusRow} role="status">
      {chip}
      {line !== null ? <span className={styles.hint}>{withBrandWord(line)}</span> : null}
    </div>
  );
};

export const AddressStatus = TypedMemo(AddressStatusComponent);
